import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, ChevronUp, Download } from "lucide-react";
import { cn } from "@/lib/utils";
import { convertLocalTimezone } from "@/utils/dateUtils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { TruncatedTextTooltip } from "@/components/ui/truncated-text-tooltip";
import { TablePagingFooter } from "@/components/widgets/common/TablePagingFooter";
import Timeline from "@/components/widgets/call-overview/Timeline";
import { WidgetTitle } from "@/components/widgets/WidgetTitle";
import { ColFilterPopover, type ColFilterEntry } from "./columnFilterShared";
import { formatDuration, formatDirectionForDisplay } from "./tableCellHelpers";
import { getDirectionColor } from "@/components/widgets/call-overview/callStatusColors";
import { useAgentCallDetails, useAgentCallFilterOptions } from "@/hooks/useAgentCallDetails";
import { useAgentCallJourneyDistinctFilter } from "@/hooks/useAgentCallJourneyDistinctFilter";
import type { AgentCallDetail } from "@/lib/axios/types/widgets.type";
import { fetchAgentCallDetails, type AgentCallSortClause, type AgentCallFilterClause } from "@/lib/axios/api/widgets.api";
import { TABLE_HEADER_TITLE_CLASS, TABLE_HEADER_ROW_CLASS, TABLE_HEADER_ROW_COLOR_CLASS, tableRowZebraClass, TableGridSkeletonRow, type TableColumnConfig } from "@/components/ui/table";
import * as XLSX from "xlsx";

function blankAware(raw: string): string {
  const t = raw.trim();
  return t === "" || t === "-" ? "-" : raw;
}

type ColKey = "startTime" | "caller" | "participant" | "direction" | "duration" | "customerName" | "companyName" | "voicemail";

const COLUMNS: { key: ColKey; title: string }[] = [
  { key: "startTime", title: "Start Time" },
  { key: "direction", title: "Direction" },
  { key: "caller", title: "Organizer" },
  { key: "participant", title: "Participant" },
  { key: "duration", title: "Call Duration" },
  { key: "customerName", title: "Customer Name" },
  { key: "companyName", title: "Company Name" },
  { key: "voicemail", title: "Voicemail" },
];

/** TCX-6406: Customer Name / Company Name are gated behind the backend's runtime toggle. */
const CUSTOMER_COMPANY_NAME_KEYS: ReadonlySet<ColKey> = new Set(["customerName", "companyName"]);

function mapColKeyToApi(key: ColKey): string {
  switch (key) {
    case "caller": return "organizer";
    case "duration": return "callDuration";
    default: return key;
  }
}


/** Raw value used for filter payloads / unique-value sets — NOT for display (see displayValueFor). */
function getColumnValue(call: AgentCallDetail, key: ColKey): string {
  switch (key) {
    case "startTime": return convertLocalTimezone(call.startTime);
    case "caller": return call.organizer;
    case "participant": return call.participant;
    case "direction": return call.direction;
    case "duration": return String(call.callDuration);
    case "customerName": return call.customerName ?? "";
    case "companyName": return call.companyName ?? "";
    case "voicemail": return call.voicemail ? "true" : "false";
  }
}

function displayValueFor(call: AgentCallDetail, key: ColKey): string {
  if (key === "duration") return formatDuration(call.callDuration);
  if (key === "direction") return formatDirectionForDisplay(call.direction);
  return blankAware(getColumnValue(call, key));
}

function renderCellContent(call: AgentCallDetail, key: ColKey) {
  if (key === "voicemail") {
    return (
      <span className="flex w-full justify-start">
        <Checkbox
          checked={call.voicemail === true}
          tabIndex={-1}
          className="h-5 w-5 rounded-md border-2 border-gray-300 dark:border-white/40 bg-transparent data-[state=checked]:bg-brand-accent data-[state=checked]:border-[#00FEC1] data-[state=checked]:text-black pointer-events-none cursor-default"
          aria-label={call.voicemail ? "Forwarded to voice mail" : "Not forwarded to voice mail"}
        />
      </span>
    );
  }
  if (key === "direction") {
    const label = displayValueFor(call, key);
    return (
      <TruncatedTextTooltip content={label} className="text-left">
        <span className={cn(getDirectionColor(label))}>{label}</span>
      </TruncatedTextTooltip>
    );
  }
  return <TruncatedTextTooltip content={displayValueFor(call, key)} className="text-left">{displayValueFor(call, key)}</TruncatedTextTooltip>;
}

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

// Old loading state was a centered "Loading..." row with `py-12` (~116px rendered) —
// keep that same height, clip skeleton rows (fixed 45px each, unchanged) to fit inside it.
const SKELETON_CONTAINER_HEIGHT = 116;
const SKELETON_ROW_HEIGHT = 45;
const SKELETON_ROW_COUNT = Math.ceil(SKELETON_CONTAINER_HEIGHT / SKELETON_ROW_HEIGHT);

interface Props {
  readonly callIds: string[];
  readonly userName: string | null;
  readonly startDate: string | undefined;
  readonly endDate: string | undefined;
  readonly isActive: boolean;
}

export function UserAgentCallsDetailTab({ callIds, userName, startDate, endDate, isActive }: Props) {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [sortKey, setSortKey] = useState<ColKey>("startTime");
  const [sortDesc, setSortDesc] = useState(true);
  const [columnFilters, setColumnFilters] = useState<Partial<Record<ColKey, string[]>>>({});
  const [expandedKeys, setExpandedKeys] = useState<ReadonlySet<string>>(() => new Set());
  const [openColumns, setOpenColumns] = useState<ReadonlySet<ColKey>>(() => new Set());
  const [searchByColumn, setSearchByColumn] = useState<Partial<Record<ColKey, string>>>({});
  const [debouncedSearchByColumn, setDebouncedSearchByColumn] = useState<Partial<Record<ColKey, string>>>({});
  const [exportBusy, setExportBusy] = useState(false);
  const searchTimersRef = useRef<Partial<Record<ColKey, ReturnType<typeof setTimeout>>>>({});

  useEffect(() => () => {
    Object.values(searchTimersRef.current).forEach((t) => t && clearTimeout(t));
  }, []);

  const handleFilterOpen = useCallback((col: string) => {
    const key = col as ColKey;
    const existing = searchTimersRef.current[key];
    if (existing) clearTimeout(existing);
    setOpenColumns((prev) => new Set(prev).add(key));
    setSearchByColumn((prev) => ({ ...prev, [key]: "" }));
    setDebouncedSearchByColumn((prev) => ({ ...prev, [key]: "" }));
  }, []);

  const handleFilterClose = useCallback((col: string) => {
    const key = col as ColKey;
    const existing = searchTimersRef.current[key];
    if (existing) clearTimeout(existing);
    setOpenColumns((prev) => { const next = new Set(prev); next.delete(key); return next; });
    setSearchByColumn((prev) => { const next = { ...prev }; delete next[key]; return next; });
    setDebouncedSearchByColumn((prev) => { const next = { ...prev }; delete next[key]; return next; });
  }, []);

  const handleFilterSearchChange = useCallback((col: string, q: string) => {
    const key = col as ColKey;
    setSearchByColumn((prev) => ({ ...prev, [key]: q }));
    const existing = searchTimersRef.current[key];
    if (existing) clearTimeout(existing);
    searchTimersRef.current[key] = setTimeout(() => {
      setDebouncedSearchByColumn((prev) => ({ ...prev, [key]: q }));
    }, 400);
  }, []);

  useEffect(() => { setPageIndex(0); setExpandedKeys(new Set()); }, [sortKey, sortDesc, columnFilters]);

  const sorts = useMemo<AgentCallSortClause[]>(
    () => [{ column: mapColKeyToApi(sortKey), direction: sortDesc ? "Desc" : "Asc" }],
    [sortKey, sortDesc]
  );

  const filters = useMemo<AgentCallFilterClause[]>(
    () => COLUMNS.map(({ key }) => ({
      column: mapColKeyToApi(key),
      values: columnFilters[key]?.length ? columnFilters[key] : null,
    })),
    [columnFilters]
  );

  const { callsPage, isCallsLoading } = useAgentCallDetails({
    callIds, pageIndex, pageSize, sorts, filters, startDate, endDate, enabled: isActive,
  });

  const customerCompanyNameEnabled = callsPage?.customerCompanyNameEnabled ?? false;

  const visibleColumns = useMemo(
    () => (customerCompanyNameEnabled ? COLUMNS : COLUMNS.filter((c) => !CUSTOMER_COMPANY_NAME_KEYS.has(c.key))),
    [customerCompanyNameEnabled]
  );

  const skeletonColumns = useMemo<TableColumnConfig[]>(
    () => [
      { title: "", key: "expand", width: 32 },
      ...visibleColumns.map((col) => ({ title: col.title, key: col.key, width: 150 })),
    ],
    [visibleColumns]
  );

  const unfilteredPage = useAgentCallFilterOptions({ callIds, startDate, endDate, enabled: isActive });

  const filterOptions = useMemo(() => {
    const result: Partial<Record<ColKey, string[]>> = {};
    const rows = unfilteredPage?.calls ?? [];
    for (const { key } of COLUMNS) {
      result[key] = Array.from(new Set(rows.map((r) => getColumnValue(r, key))));
    }
    return result;
  }, [unfilteredPage]);

  const apiColumnFilters = useMemo<Partial<Record<string, string[] | null>>>(
    () => Object.fromEntries(
      COLUMNS.map(({ key }) => [mapColKeyToApi(key), columnFilters[key]?.length ? columnFilters[key] : null])
    ),
    [columnFilters]
  );

  const openApiColumns = useMemo(
    () => Array.from(openColumns).map(mapColKeyToApi),
    [openColumns]
  );

  const apiSearchByColumn = useMemo<Partial<Record<string, string>>>(
    () => Object.fromEntries(
      Array.from(openColumns).map((key) => [mapColKeyToApi(key), debouncedSearchByColumn[key] ?? ""])
    ),
    [openColumns, debouncedSearchByColumn]
  );

  const distinctQuery = useAgentCallJourneyDistinctFilter({
    callIds,
    openColumns: openApiColumns,
    searchByColumn: apiSearchByColumn,
    columnFilters: apiColumnFilters,
    startDate,
    endDate,
    enabled: isActive,
  });

  const mergedFilterOptions = useMemo(() => {
    const result = { ...filterOptions };
    for (const key of openColumns) {
      result[key] = distinctQuery.getState(mapColKeyToApi(key)).values;
    }
    return result;
  }, [filterOptions, openColumns, distinctQuery]);

  const handleSort = useCallback((key: ColKey) => {
    if (key === sortKey) setSortDesc((d) => !d);
    else { setSortKey(key); setSortDesc(true); }
  }, [sortKey]);

  const handleToggleExpand = useCallback((rowKey: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey); else next.add(rowKey);
      return next;
    });
  }, []);

  const handleToggleFilterValue = useCallback((key: string, val: string) => {
    setColumnFilters((prev) => {
      const cur = prev[key as ColKey] ?? [];
      const next = cur.includes(val) ? cur.filter((v) => v !== val) : [...cur, val];
      return { ...prev, [key]: next };
    });
  }, []);

  const handleClearColumnFilter = useCallback((key: string) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[key as ColKey];
      return next;
    });
  }, []);

  const handleSelectAllColumnValues = useCallback((key: string, vals: string[]) => {
    setColumnFilters((prev) => ({ ...prev, [key]: vals }));
  }, []);

  const rows = callsPage?.calls ?? [];
  const total = callsPage?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleExport = useCallback(async () => {
    if (!startDate || !endDate) return;
    try {
      setExportBusy(true);
      const all = await fetchAgentCallDetails({ callIds, pageIndex: 0, pageSize: null, sorts: [], filters: [], startTime: startDate, endTime: endDate });
      // TCX-6406 Amendment: Customer Name / Company Name are shown on-screen but excluded from export.
      const headers = ["Call Queue", "Start Time", "Organizer", "Participant", "Direction", "Waiting Duration", "Talking Duration", "Call Duration", "Voicemail"];
      const dataRows = all.calls.map((c) => [
        c.cqDirectline ?? "",
        convertLocalTimezone(c.startTime),
        c.organizer,
        c.participant,
        formatDirectionForDisplay(c.direction),
        formatDuration(c.waitingDuration ?? 0),
        formatDuration(c.talkingDuration ?? 0),
        formatDuration(c.callDuration),
        c.voicemail ? "yes" : "no",
      ]);
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
      XLSX.utils.book_append_sheet(wb, ws, "Calls Detail");
      const dateStr = new Date().toISOString().split("T")[0].replace(/-/g, "");
      const safeName = (userName ?? "user").trim().replace(/\s+/g, "_");
      XLSX.writeFile(wb, `${safeName}_calls_${dateStr}.csv`);
    } finally {
      setExportBusy(false);
    }
  }, [callIds, userName, startDate, endDate]);

  return (
    // eslint-disable-next-line no-restricted-syntax -- Card container border/bg, not table row/header coloring.
    <Card className="w-full h-full flex flex-col overflow-hidden dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] rounded-lg">
      <CardHeader className="flex flex-row items-center justify-between sticky top-0 z-30 border-b border-border rounded-t-lg flex-shrink-0">
        <WidgetTitle>Call Journey</WidgetTitle>
        <Button
          variant="outline"
          size="sm"
          // eslint-disable-next-line no-restricted-syntax -- Export button chrome, not table row/header coloring.
          className="text-xs flex items-center gap-1 [text-decoration:none] hover:bg-brand-accent hover:border-[#00FEC1] hover:text-black transition-colors dark:bg-[#0C3451] dark:border-[#4A6B85] dark:text-white dark:hover:bg-brand-accent dark:hover:border-[#00FEC1] dark:hover:text-black"
          onClick={handleExport}
          disabled={exportBusy}
        >
          <Download className="h-3 w-3" />
          {exportBusy ? "Exporting..." : "Export"}
        </Button>
      </CardHeader>
      <CardContent className="p-0 flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className="flex-1 min-h-0 overflow-auto">
          <div className="min-w-full inline-block align-top">
            <div className="table w-full call-journey-table" style={{ borderCollapse: "collapse", borderSpacing: 0, tableLayout: "fixed" }}>
              {/* Header Row */}
              <div className={cn("table-row text-xs", TABLE_HEADER_ROW_CLASS, TABLE_HEADER_TITLE_CLASS)}>
                <div
                  className={cn("table-cell h-[45px] px-1 text-center align-middle sticky left-0 z-40 font-bold border-t-0 border-l-0 border-r-0 transition-colors", TABLE_HEADER_ROW_COLOR_CLASS)}
                  style={{ width: 32 }}
                />
                {visibleColumns.map((col) => {
                  const isOpen = openColumns.has(col.key);
                  const colState = isOpen ? distinctQuery.getState(mapColKeyToApi(col.key)) : undefined;
                  const entry: ColFilterEntry = {
                    options: mergedFilterOptions[col.key] ?? [],
                    loading: colState?.isLoading ?? false,
                    hasMore: colState?.hasMore ?? false,
                    // -1 ("not loaded") only until the first response; a re-query (search/filter) must not
                    // flip it back or ColFilterPopover treats it as an external reset and clears the search.
                    loadedPage: colState?.isLoading && !colState.loaded ? -1 : 0,
                    searchQuery: isOpen ? (searchByColumn[col.key] ?? "") : "",
                  };
                  return (
                    <div
                      key={col.key}
                      className="table-cell h-[45px] px-2 text-left align-middle border-b border-l-0 border-r-0 border-gray-300 dark:border-0 transition-colors"
                      style={{ width: `${100 / visibleColumns.length}%` }}
                    >
                      <div className="flex items-center justify-start gap-1">
                        <button type="button" className="flex items-center gap-1 cursor-pointer" onClick={() => handleSort(col.key)}>
                          <span className="whitespace-nowrap">{col.title}</span>
                          {sortKey === col.key && (sortDesc ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
                        </button>
                        <ColFilterPopover
                          columnKey={col.key}
                          selectedValues={columnFilters[col.key] ?? []}
                          entry={entry}
                          onOpen={() => handleFilterOpen(col.key)}
                          onClose={() => handleFilterClose(col.key)}
                          onSearchChange={handleFilterSearchChange}
                          onLoadMore={() => distinctQuery.loadMore(mapColKeyToApi(col.key))}
                          onToggleValue={handleToggleFilterValue}
                          onClearFilter={handleClearColumnFilter}
                          onSelectAll={handleSelectAllColumnValues}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Data Rows */}
              <div className="table-row-group border-gray-300 dark:border-0">
                {(() => {
                    if (isCallsLoading) {
                      return Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
                        <TableGridSkeletonRow key={i} columns={skeletonColumns} />
                      ));
                    }
                    if (rows.length === 0) {
                      return (
                        <tr className="table-row">
                          <td className="table-cell text-center align-middle py-12 text-muted-foreground dark:text-white" colSpan={visibleColumns.length + 1}>
                            <p className="text-sm">No call records were found</p>
                          </td>
                        </tr>
                      );
                    }
                    return rows.map((call, idx) => {
                      const rowKey = String(idx);
                      const expanded = expandedKeys.has(rowKey);
                      const cellBorder = "border-b";
                      return (
                        <Fragment key={rowKey}>
                          <div className={cn("table-row transition-colors border-gray-300 dark:border-0", cellBorder, tableRowZebraClass(idx))}>
                            <div
                              className={cn("table-cell h-[45px] px-1 border-l-0 border-r-0 border-gray-300 dark:border-0 align-middle sticky left-0 z-20", cellBorder)}
                              style={{ width: 32 }}
                            >
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 hover:bg-brand-accent/90 hover:text-white transition-all duration-200 flex items-center justify-center"
                                onClick={() => handleToggleExpand(rowKey)}
                                aria-label={expanded ? "Collapse call journey" : "Expand call journey"}
                              >
                                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </Button>
                            </div>
                            {visibleColumns.map((col) => (
                              <div
                                key={col.key}
                                className={cn("table-cell h-[45px] px-2 text-xs text-primary dark:text-white border-gray-300 dark:border-0 transition-colors align-middle border-l-0 border-r-0", cellBorder)}
                                style={{ width: `${100 / visibleColumns.length}%` }}
                              >
                                {renderCellContent(call, col.key)}
                              </div>
                            ))}
                          </div>
                          {expanded && (
                            <tr className="table-row">
                              {/* eslint-disable-next-line no-restricted-syntax -- expanded detail panel background, not zebra row/header coloring. */}
                              <td className="table-cell align-middle py-2 text-muted-foreground bg-white dark:bg-[#3C5C73] dark:text-white overflow-hidden" colSpan={visibleColumns.length + 1}>
                                {call.journey.length === 0 ? (
                                  <div className="text-xs text-muted-foreground text-center">No journey events.</div>
                                ) : (
                                  <div className="w-full overflow-x-auto">
                                    <Timeline events={call.journey} />
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          </div>
        <TablePagingFooter
          currentPage={pageIndex + 1}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          onPageChange={(p) => { setPageIndex(p - 1); setExpandedKeys(new Set()); }}
          isLoading={isCallsLoading}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageSizeChange={(size) => { setPageSize(size); setPageIndex(0); setExpandedKeys(new Set()); }}
        />
      </CardContent>
    </Card>
  );
}
